/**
 * InactivityWatcher
 *
 * Classe para ser utilizada ao precisar monitorar tempo de inatividade do usuário
 * Ele é iniciado com os valores passados como parâmetro e de tanto em tanto tempo verifica
 * no localStorage se o usuário teve alguma interação com a tela. Caso tenha, chama o método
 * onActivity (opcional) e caso não tenha interação nesse determinado tempo ele para o
 * intervalo, limpa os eventos e chama a função onInactivity.
 *
 * Como utilizar:
 * 1. Importe a classe
 * import inactivityWatcher from '@/utils/watchers/InactivityWatcher'
 * 2. Comece o watcher passando os parâmetros necessários (ou deixar os valores padrão)
 * 3. Parar o watcher quando necessário ou no destroy dos componentes
 * inactivityWatcher.stopMonitoring()
 *
 */

interface Params {
  inactivityTime: number // Max time for inactivity (default 7200000)
  intervalTime: number // Time to inteval check inactivity (default 60000)
  onActivity?: () => void | undefined // Function to call when interval detect activity
  onInactivity?: () => void | undefined // Function to call when interval detect inactivity
}

class InactivityWatcher {
  private static instance: InactivityWatcher
  private localStorageKey = 'EHR_last_activity_time'
  private events = ['mousemove', 'scroll', 'keydown']
  private interval: number | undefined
  private inactivityTime = 7200000
  private intervalTime = 60000
  private onInactivity: (() => void) | undefined = undefined
  private onActivity: (() => void) | undefined = undefined
  private timeoutTracker: number | undefined
  private eventHandler: () => void = () => undefined

  public static getWatcher(): InactivityWatcher {
    if (!InactivityWatcher.instance) {
      InactivityWatcher.instance = new InactivityWatcher()
      return InactivityWatcher.instance
    }
    return InactivityWatcher.instance
  }

  public initMonitoring({
    inactivityTime = 14400000, // 4 hours
    intervalTime = 60000, //60 seconds
    onActivity,
    onInactivity
  }: Params) {
    this.inactivityTime = inactivityTime
    this.intervalTime = intervalTime
    this.onActivity = onActivity
    this.onInactivity = onInactivity
    this.eventHandler = this.updateLastActivityTime.bind(this)
    this.listenUserEvents()
    this.startInterval()
  }

  private setUpdatedTime() {
    localStorage.setItem(this.localStorageKey, String(Date.now()))
  }

  private updateLastActivityTime() {
    if (this.timeoutTracker) clearTimeout(this.timeoutTracker)
    this.timeoutTracker = setTimeout(() => {
      this.setUpdatedTime()
    }, 500) // Update every 0,5 seconds
  }

  private listenUserEvents() {
    this.events.forEach(type => {
      window.addEventListener(type, this.eventHandler)
    })
  }

  private startInterval() {
    this.setUpdatedTime() //Initialize local storage value

    this.interval = setInterval(() => {
      // Do something after {inactivityTime} of inactivity
      const expirationTime =
        Number(localStorage.getItem(this.localStorageKey) || 0) +
        this.inactivityTime
      if (expirationTime < Date.now()) {
        this.stopMonitoring()
        if (this.onInactivity) this.onInactivity() //Do something on inactivity (optional)
        return
      }
      if (this.onActivity) this.onActivity() //Do something on activity (optional)
    }, this.intervalTime) // Check every {intervalTime}
  }

  public stopMonitoring() {
    if (this.interval) clearInterval(this.interval)
    this.events.forEach(type => {
      window.removeEventListener(type, this.eventHandler)
    })
    this.resetLastActivityTime()
  }

  private resetLastActivityTime() {
    localStorage.removeItem(this.localStorageKey)
  }
}

const watcher = InactivityWatcher.getWatcher()

export default watcher
